import { LinkCell } from 'components/link-cell';
import {
  type ReactNode,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { debounce } from 'lodash-es';
import { Button, Link as CarbonLink, Tooltip } from '@carbon/react';
import {
  Checkmark,
  DataView,
  Edit,
  WarningAltFilled,
} from '@carbon/react/icons';
import { useStore } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import type { AvailableAfe } from 'types/api-responses';
import {
  DataTable,
  type ColumnType,
  type DataTableColumnDef,
} from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { Link } from 'components/link';
import { TypeCurveVerificationStatusIndicator } from 'components/type-curve-verification-status/type-curve-verification-status';
import { CompositeLegLines } from 'components/composite-leg-lines';
import { legEditRow, targetsCompositeLeg } from './composite-leg-edit';
import { VOLUME_OVERRIDE_FIELDS } from './slot-tearsheet/constants';
import { formatCellValue } from 'components/data-table/utils';
import { EditableDateCell } from 'components/editable-date-cell/editable-date-cell';
import { EditPencilButton } from 'components/edit-pencil-button/edit-pencil-button';
import {
  EditAfeSourceModalView,
  type EditAfeSourceModalViewProps,
} from 'pages/packages/show/edit-afe-source-modal/edit-afe-source-modal-view';
import {
  BulkEditSlotsPanel,
  FIELD_LABELS,
  type BulkEditScopedSavePayload,
} from 'components/nri-table/bulk-edit-slots';
import type { TractNRI } from 'components/nri-table/types';
import { SlotExpandedRow } from './slot-expanded-row';
import { unitAndInterestColumns } from './slot-column-utils';
import { SlotTearsheet } from './slot-tearsheet/slot-tearsheet';
import { EditVolumeSourcePickerModal } from './edit-volume-source-picker-modal';
import {
  currentVolumeSourceForRow,
  volumeOverrides,
  type CurrentVolumeSource,
} from './volume-source-utils';
import { commitOverrideToWorkingConfig } from './override-commit';
import { NO_CASE_HINT } from './view-property-schedule';
import { unvaluedReasonColumn } from './unvalued-reason-column-utils';
import {
  scheduleDialogOpenAtom,
  scheduleFocusSearchAtom,
} from './schedule-dialog/atoms';
import {
  useAfeCatalogSearch,
  useAvailableAfes,
  useBulkAvailableAfes,
  useMasterDataView,
  useVolumeSourcePickerData,
  type WorkingConfigForm,
} from './hooks';
import {
  EMPTY,
  formatAssetType,
  linkCellRenderer,
  typeCurveAreaLink,
  locateColumn,
  MASTER_DATA_PAGE_SIZES,
  paintNewestOperation,
  reconcileSelectedRows,
  slotIdDisplayText,
  pairFromRow,
  type RowVolumeSourcePair,
  slotIdLink,
  toTableFilters,
  useMasterDataVisibleColumns,
  wellStatusColumn,
} from './utils';
import {
  DEFAULT_VALUED_COLUMN_FILTERS,
  DEFAULT_VALUED_TABLE_FILTERS,
  VALUED_FILTER_META,
  valuedCell,
} from './valued-column-utils';
import {
  GRID_SYNCED_MILESTONES,
  MILESTONE_WIRE_FIELD,
  type GridSyncedMilestone,
} from './schedule-dialog/constants';
import type {
  CostDateField,
  MasterDataResponse,
  MasterDataViewProps,
  SlotDateField,
  SlotOverrideOperation,
  SlotRow,
} from './types';
import type { SlotOverrideRejection } from './schedule-dialog/types';
import {
  evalSettingsPanelOpenAtom,
  scheduleOverrideRejectionsAtom,
} from './atoms';
import { DpsAreaCell } from './dps-area-cell';
import styles from './slot-view.module.scss';

/**
 * DataTable needs an index signature on its row type; `SlotRow` (an interface)
 * doesn't carry one, so intersect it here — the same pattern AssetView uses.
 */
type SlotTableRow = SlotRow & Record<string, unknown>;

type SlotColumnKey = keyof SlotRow & string;

/**
 * Human reason shown on a rejected date cell (INS-12497). One per blocking code the BE
 * write validator emits; the aggregate `ScheduleCalculateRejectedError`
 * toast carries the summary, this the per-cell detail.
 */
const REJECTION_REASON: Record<SlotOverrideRejection['code'], string> = {
  locked_reported:
    'This milestone is reported for the well and can’t be overridden — the last calculation did not apply this edit.',
  out_of_order:
    'This date breaks the permit → spud → completion → first-production order — the last calculation did not apply this edit.',
  invalid_date:
    'This value is not a date — the last calculation did not apply this edit.',
  unknown_afe:
    'This AFE does not exist — the last calculation did not apply this edit.',
};

// Snake wire field ← camel milestone key, widened for a lookup by any column key
// (cost-date columns aren't milestones and resolve to undefined — never rejected).
const wireFieldFor = (accessorKey: string): string | undefined =>
  (MILESTONE_WIRE_FIELD as Record<string, string | undefined>)[accessorKey];

/** The decoration a slot date cell shows — see {@link slotDateCellState}. */
export type SlotDateCellState =
  | { kind: 'rejected'; code: SlotOverrideRejection['code'] }
  | { kind: 'overridden' }
  | { kind: 'none' };

/**
 * Which decoration an inline slot date cell shows (INS-12497). A Calculate rejection
 * WINS over the optimistic override ✓: a write the last run refused (locked_reported /
 * out_of_order) must read as rejected, never as an applied override — otherwise the grid
 * asserts a date the calculation never used. Cost-date columns aren't milestones, so they
 * map to no wire field and are never "rejected". A row with no slotId shows nothing.
 *
 * @param {object} params - slotId, the column key, the row's overriddenFields, and the
 *   per-cell rejection lookup (keyed `slotId:wire_field`).
 * @return {SlotDateCellState} The decoration to render.
 */
export function slotDateCellState({
  slotId,
  accessorKey,
  overriddenFields,
  rejectedCells,
}: {
  slotId: string | undefined;
  accessorKey: string;
  overriddenFields: string[] | undefined;
  rejectedCells: ReadonlyMap<string, SlotOverrideRejection['code']>;
}): SlotDateCellState {
  if (!slotId) {
    return { kind: 'none' };
  }
  const wireField = wireFieldFor(accessorKey);
  const code = wireField
    ? rejectedCells.get(`${slotId}:${wireField}`)
    : undefined;
  if (code) {
    return { kind: 'rejected', code };
  }
  if (overriddenFields?.includes(accessorKey)) {
    return { kind: 'overridden' };
  }
  return { kind: 'none' };
}

/**
 * Drops the rejections a new override operation supersedes (INS-12497): editing a cell
 * is a fresh, unvalidated value, so its stale "rejected" mark must clear now rather than
 * linger until the next Calculate. A single-slot / id-list edit clears the exact
 * (slot, field) pairs it writes; a filter-scoped edit clears every rejection on the
 * written field (its scope isn't enumerated here). A pure volume/AFE edit writes no date
 * field and clears nothing. Returns the same reference when nothing changed (stable for
 * React).
 *
 * @param {SlotOverrideRejection[]} rejections - The current rejections.
 * @param {SlotOverrideOperation} operation - The override just committed.
 * @return {SlotOverrideRejection[]} The surviving rejections.
 */
export function clearRejectionsForOperation(
  rejections: readonly SlotOverrideRejection[],
  operation: SlotOverrideOperation
): readonly SlotOverrideRejection[] {
  if (rejections.length === 0) {
    return rejections;
  }
  // A revert names its fields instead of carrying values, and it re-edits the cell as much as
  // a new value does, so it clears the mark too.
  const touched =
    operation.kind === 'unset'
      ? operation.fields
      : Object.keys(operation.overrides);
  const fields = new Set(
    touched.map(wireFieldFor).filter((wire): wire is string => Boolean(wire))
  );
  if (fields.size === 0) {
    return rejections;
  }
  const slotIds =
    operation.kind === 'slot' || operation.kind === 'unset'
      ? new Set([operation.slotId])
      : operation.kind === 'ids'
        ? new Set(operation.slotIds)
        : null;
  const next = rejections.filter((rejection) => {
    if (!fields.has(rejection.field)) {
      return true;
    }
    return slotIds === null ? false : !slotIds.has(rejection.slotId);
  });
  return next.length === rejections.length ? rejections : next;
}

/**
 * Slot ID cell: TWO CONTROLS, because they go to two different places.
 *
 * ── WHAT THIS REVERSES, AND WHY THAT IS ALLOWED ──────────────────────────────────────
 *
 * 61d6c0a895 made the whole cell — text and icon — one Carbon `Link` that opened the
 * valuation tearsheet, moving well-page navigation to the Well Name column. That was a
 * deliberate choice on this same ticket, and it cost the Properties table its link to the
 * well: the Slot ID is the identifier a reader clicks to go and look at the well itself.
 * Sam has decided the two belong apart, and it is our own commit on our own ticket, so
 * there is no other team's decision being undone here.
 *
 *   the TEXT  → `slotIdLink(row)`, the well page, exactly as it was before that commit
 *   the ICON  → the tearsheet
 *
 * The Well Name column keeps its own well link. Two paths to the same page is fine — they
 * are the two identifiers a reader recognises a row by.
 *
 * ── ONLY THE ICON DISABLES ───────────────────────────────────────────────────────────
 *
 * `disabled` means "no completed calculation", because the tearsheet reads that
 * calculation's frozen blob. THE WELL PAGE HAS NOTHING TO DO WITH THAT. When the flag
 * applied to the whole cell, a package with no calculation lost its link to the well as
 * collateral — a live navigation greyed out by an unrelated condition. So the flag now
 * reaches the icon alone, and keeps its own explanation in the title.
 *
 * A row with no `slotId` or no display text renders the empty indicator and neither
 * control. `linkCellRenderer` already degrades to plain text when `slotIdLink` resolves to
 * nothing, so the text is never a dead link.
 *
 * EXPORTED FOR TEST, like overlayScheduleDateOverrides below. The table body is
 * virtualised, and jsdom gives it no viewport — measured: a one-row response paints zero
 * `tbody tr`, so no integration render can reach this cell. Testing it through the table
 * would be a test that passes because it asserts nothing.
 *
 * @param {object} props - The row, the open handler, and the disabled flag.
 * @return {JSX.Element} The Slot ID cell: a well link and a valuation-inputs icon.
 */
export function SlotIdCell({
  row,
  onOpen,
  disabled,
}: {
  row: SlotRow;
  onOpen: (row: SlotRow) => void;
  disabled: boolean;
}) {
  const text = slotIdDisplayText(row);
  if (!row.slotId || !text) {
    return <>{EMPTY}</>;
  }
  const iconLabel = disabled
    ? 'Run a calculation to view valuation inputs'
    : 'View valuation inputs';
  return (
    <span className={styles.slotIdCell}>
      {linkCellRenderer(text, slotIdLink(row))}
      {/* The icon carries no text, so `title` alone would leave it unnamed for a screen
          reader — hence the explicit aria-label. Still a `Link` rather than a Button: it
          keeps the mock's `cds--link__icon` treatment and stays on the row's line height,
          which a ghost icon-button does not. */}
      <CarbonLink
        href="#"
        className={styles.slotIdTearsheetIcon}
        renderIcon={DataView}
        disabled={disabled}
        title={iconLabel}
        aria-label={iconLabel}
        onClick={(event) => {
          event.preventDefault();
          if (!disabled) {
            onOpen(row);
          }
        }}
      />
    </span>
  );
}

/**
 * Trailing edit pencil on the four lifecycle-milestone date cells (permit, spud, completion,
 * first production) — INS-12497. It opens the shared "View property schedule" Gantt dialog
 * (mounted in the Case-management panel), so a property's milestone dates are reviewed and
 * changed there rather than edited inline.
 *
 * Gated on a selected evaluation case, mirroring the Case-management panel's own button: the
 * Gantt cannot render without a case, so until one is picked the pencil is disabled and says
 * why.
 *
 * EXPORTED FOR TEST, like SlotIdCell: the table body is virtualised and jsdom gives it no
 * viewport, so the enabled/click path is also unit-tested here directly.
 *
 * @param {object} props - The column header (labels the affordance), the disabled flag (no
 *   case selected), and the open handler.
 * @return {JSX.Element} The pencil button.
 */
export function DateCellScheduleButton({
  header,
  disabled,
  onOpen,
}: {
  header: string;
  disabled: boolean;
  onOpen: () => void;
}) {
  const button = (
    <Button
      hasIconOnly
      kind="ghost"
      size="sm"
      className={styles.dateCellScheduleButton}
      disabled={disabled}
      renderIcon={Edit}
      iconDescription={`View property schedule (${header})`}
      tooltipPosition="left"
      onClick={onOpen}
    />
  );
  if (!disabled) {
    return button;
  }
  // A disabled Button emits no hover and can't take focus, so its own `iconDescription`
  // tooltip never shows and reaches only screen readers. A focusable span wraps it as the
  // Tooltip trigger so the "select a case" hint stays discoverable on hover / keyboard —
  // the same pattern (and the same shared NO_CASE_HINT) the panel button uses (INS-12497).
  return (
    <Tooltip label={NO_CASE_HINT} align="top">
      <span className={styles.dateCellScheduleTrigger} tabIndex={0}>
        {button}
      </span>
    </Tooltip>
  );
}

/**
 * Whether the row's OWN Volume Source is overridden (a whole-row override, e.g. a bulk edit that
 * selected a composite wellbore). The Formation, Volume Source and Forecast Status cells all read
 * this: under it a composite prices on the one source the row names, so all three show the row's
 * values rather than a line per leg.
 *
 * @param {SlotRow} row - The row.
 * @return {boolean} True when `volumeSourceId` is among the row's overridden fields.
 */
function rowVolumeSourceOverridden(row: SlotRow): boolean {
  return Boolean(
    row.slotId && row.overriddenFields?.includes('volumeSourceId')
  );
}

/** Which record-picker modal is open (one at a time), with its seeded selection. */
type SlotEdit =
  | {
      kind: 'volume';
      slotId: string;
      wellName?: string;
      /** Whether the slot is a well, as the backend reports it on the row. */
      isWellSlot: boolean;
      currentId?: string;
      /** The row's source name, type and href, pinned into its list when the list lacks it. */
      currentSource: CurrentVolumeSource;
      pair: RowVolumeSourcePair;
      /**
       * Whether the modal offers Reset: a composite wellbore's LEG that carries an override.
       * A row's own override is reverted from the tearsheet; a leg has no tearsheet, so the
       * drawer that set its override is where it is dropped.
       */
      resettable?: boolean;
    }
  | { kind: 'afe'; slotId: string; wellName?: string; currentId?: string };

/** Slot view also receives the working-config form (it uses the slot-override setters). */
export interface SlotViewProps extends MasterDataViewProps {
  workingConfig: WorkingConfigForm;
}

/**
 * Date columns carry the inline Start/End range filter, which packs two
 * "MM/DD/YYYY" inputs side-by-side — so they're widened to fit both, mirroring
 * AssetView's 220px created/updated date columns.
 */
const DATE_COLUMN_SIZE = 220;

/**
 * Column whose value is rendered by DataTable's built-in `meta.type` formatter.
 *
 * @param {string} accessorKey - The `SlotRow` field.
 * @param {string} header - The column header label.
 * @param {number} size - The column width in px.
 * @param {ColumnType} type - The DataTable column type driving formatting + alignment.
 * @return {object} The DataTable column definition.
 */
function column(
  accessorKey: SlotColumnKey,
  header: string,
  size: number,
  type: ColumnType
): DataTableColumnDef<SlotTableRow> {
  // Date/datetime columns opt into the inline Start/End range filter via
  // `dateFilterLocalTime` — the same local-time single-range picker AssetView
  // uses, which repaints the End bound reliably (the two-single-pickers default
  // left End blank until a remount). Every other type keeps a bare `{ type }`.
  const isDate = type === 'date' || type === 'datetime';
  return {
    accessorKey,
    header,
    size,
    meta: isDate ? { type, dateFilterLocalTime: true } : { type },
  };
}

/**
 * A scenario-area column: the area NAME, linked to the scenario whose area it is,
 * opening in a new tab. Spreads column() so sorting and the text filter behave
 * exactly like every other text column; only the cell changes.
 *
 * linkCellRenderer falls back to plain text when the href is missing, which is
 * what an eval captured before hrefs were recorded will hit.
 *
 * @param {string} accessorKey - The area-name field.
 * @param {string} hrefKey - The paired href field.
 * @param {string} header - The column header label.
 * @param {number} size - The column width in px.
 * @return {object} The DataTable column definition.
 */
function areaColumn(
  accessorKey: SlotColumnKey,
  hrefKey: keyof SlotTableRow,
  header: string,
  size: number
): DataTableColumnDef<SlotTableRow> {
  return {
    ...column(accessorKey, header, size, 'text'),
    cell: (info) =>
      linkCellRenderer(
        info.getValue<string>(),
        info.row.original[hrefKey] as string | null | undefined
      ),
  };
}

interface EditableSourceCellProps {
  /** Whether the value is overridden (shows the checkmark). */
  isOverridden: boolean;
  /** Accessible label for the overridden checkmark. */
  overriddenLabel: string;
  /** The displayed value; a node when the value opens something (a composite's legs). */
  displayValue: ReactNode | undefined;
  /** When set (with a value), render the value as a link. */
  href?: string;
  /** Optional content before the value (e.g. a verification indicator). */
  leading?: ReactNode;
  /** Edit pencil label. */
  editLabel: string;
  /** Whether the cell is editable (shows the pencil). */
  canEdit: boolean;
  /** Opens the editor for this cell. */
  onEdit: () => void;
  /** Wrap the value instead of truncating it (a composite wellbore's leg line). */
  wrapText?: boolean;
}

/**
 * Shared renderer for the two "record picker" Slot columns (Volume Source, AFE
 * Source): an optional overridden checkmark, an optional leading indicator, the
 * value (as a link when an href is present), and an edit pencil. The value/href/
 * override flags are computed per-column by the caller.
 *
 * @param {EditableSourceCellProps} props - Display value, override state, and edit handler.
 * @return {JSX.Element} The source cell.
 */
function EditableSourceCell({
  isOverridden,
  overriddenLabel,
  displayValue,
  href,
  leading,
  editLabel,
  canEdit,
  onEdit,
  wrapText = false,
}: EditableSourceCellProps) {
  return (
    <div className={styles.editCell}>
      {isOverridden && (
        <Checkmark
          className={styles.overrideIcon}
          aria-label={overriddenLabel}
        />
      )}
      <span
        className={wrapText ? styles.editCellTextWrap : styles.editCellText}
      >
        {leading}
        {href && displayValue ? (
          <Link to={href} openInNewTab>
            {displayValue}
          </Link>
        ) : (
          (displayValue ?? EMPTY)
        )}
      </span>
      {canEdit && <EditPencilButton label={editLabel} onClick={onEdit} />}
    </div>
  );
}

/** The grid columns a dialog date override can paint: the 3 editable milestones + the read-only Completion column. */
type GridDatePatch = Partial<
  Pick<SlotRow, GridSyncedMilestone | 'completionDate'>
>;

/**
 * [wire snake key, grid/camel column] for each date the schedule dialog can override and the
 * Properties grid mirrors for DISPLAY. The three editable milestones DERIVE from the single
 * source ({@link GRID_SYNCED_MILESTONES}) + the wire mapping ({@link MILESTONE_WIRE_FIELD}).
 * Modeled completion is appended for DISPLAY ONLY: the dialog's completion override (wire
 * `completion_date`) surfaces in the read-only Completion column so a dialog completion edit
 * reflects live in the table too — matching the projected completion the server later resolves
 * into `completionDate` (SlotAttributes). It is NOT in GRID_SYNCED_MILESTONES because the grid
 * can't originate a completion edit (no round-trip), so it is never pruned. INS-10671.
 */
const OVERLAY_DATE_FIELDS: ReadonlyArray<
  readonly [wire: string, grid: keyof GridDatePatch]
> = [
  ...GRID_SYNCED_MILESTONES.map(
    (milestone): readonly [wire: string, grid: keyof GridDatePatch] => [
      MILESTONE_WIRE_FIELD[milestone],
      milestone,
    ]
  ),
  [MILESTONE_WIRE_FIELD.modeledCompletionDate, 'completionDate'],
];

/**
 * Overlays the schedule dialog's committed per-slot date edits (`scheduleSlotOverrides`) onto
 * the Properties grid rows, so a dialog date edit reflects LIVE in the table — the mirror of
 * view-property-schedule's `deriveSlotDateOverrides` (grid → dialog).
 *
 * The two bridges are deliberately NOT merged: they translate opposite directions between the
 * two override channels and operate on different in/out shapes (this one: raw wire ops →
 * `SlotRow[]`; the other: `SlotOverrideOperation[]` → `ScheduleOverrideEntry[]`), so a single
 * generic helper would take more parameters than it saves. They stay in sync structurally via
 * the shared, single-sourced {@link GRID_SYNCED_MILESTONES} (the two-channel split they bridge
 * is itself the root cause — see the channel rationale on WorkingConfig.scheduleSlotOverrides).
 * Only the `individual` ops for the fields in {@link OVERLAY_DATE_FIELDS} apply — the three
 * editable milestones plus modeled completion (display-only, into the read-only Completion
 * column); bulk/preserved ops are already resolved server-side via `slotOverrides`. Later ops
 * win, matching the Calculate merge
 * (`scheduleSlotOverrides` applied after `slotOverrides`); the touched fields join
 * `overriddenFields` so the cell reads as overridden.
 *
 * @param {SlotRow[]} rows - The master_data rows (with `slotOverrides` already applied).
 * @param {Array<Record<string, unknown>>} scheduleSlotOverrides - The dialog's committed date ops.
 * @return {SlotRow[]} The rows with the dialog's date edits overlaid (unchanged rows kept by reference).
 */
export function overlayScheduleDateOverrides(
  rows: SlotRow[],
  scheduleSlotOverrides: Array<Record<string, unknown>>
): SlotRow[] {
  const bySlot = new Map<string, GridDatePatch>();
  for (const op of scheduleSlotOverrides) {
    if (op.type !== 'individual' || typeof op.slot_id !== 'string') {
      continue;
    }
    const overrides = op.overrides as Record<string, unknown> | undefined;
    if (!overrides) {
      continue;
    }
    for (const [wireField, gridField] of OVERLAY_DATE_FIELDS) {
      const value = overrides[wireField];
      if (typeof value === 'string') {
        const patch = bySlot.get(op.slot_id) ?? {};
        patch[gridField] = value;
        bySlot.set(op.slot_id, patch);
      }
    }
  }
  if (bySlot.size === 0) {
    return rows;
  }
  return rows.map((row) => {
    const patch = row.slotId ? bySlot.get(String(row.slotId)) : undefined;
    if (!patch) {
      return row;
    }
    const overriddenFields = Array.from(
      new Set([...(row.overriddenFields ?? []), ...Object.keys(patch)])
    );
    return { ...row, ...patch, overriddenFields };
  });
}

/**
 * Slot view of the Master Data Panel. Renders the ported Slots columns with
 * per-cell edit (read-only data in P5 terms — overrides are captured in a
 * TanStack Form and overlaid client-side, not yet sent to a backend). Volume
 * Source / AFE Source edit via a modal (record picked from a per-slot fetched
 * list); the 8 cost/slot dates edit inline. Server pagination/sort/search come
 * from `useMasterDataView`.
 *
 * @param {SlotViewProps} props - The page identity, the view-selector dropdown, and the override form.
 * @return {JSX.Element} The Slot data table.
 */
export function SlotView({
  packageId,
  calculationId,
  viewDropdown,
  workingConfig,
}: SlotViewProps) {
  // INS-10777 — pending override operations, read via a narrow selector so a slot edit
  // re-renders only the table, not the Eval Settings panel or unrelated form fields.
  const slotOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.slotOverrides
  );
  // The form's state updates synchronously on append, so a refetch reads the log as it
  // is now rather than as it was at the last render.
  const { form } = workingConfig;
  const readSlotOverrides = useCallback(
    () => form.state.values.slotOverrides,
    [form]
  );
  // The schedule dialog's date edits ride a separate channel; read them so a dialog edit
  // overlays LIVE onto the grid (the mirror of the grid→dialog bridge). INS-10671.
  const scheduleSlotOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.scheduleSlotOverrides
  );
  // The milestone date cells open the shared "View property schedule" Gantt dialog, which
  // needs a selected evaluation case to render — so their pencil is disabled until one is
  // picked, mirroring the Case-management panel's own button (INS-12497).
  const evaluationCaseId = useStore(
    workingConfig.form.store,
    (state) => state.values.evaluationCaseId
  );
  const caseSelected = evaluationCaseId !== null;
  // Opens the shared schedule dialog (mounted in the Case-management panel) from a date cell.
  // `useSetAtom` returns a stable setter, so it is memo-dependency safe.
  const openScheduleDialog = useSetAtom(scheduleDialogOpenAtom);
  // The schedule dialog is mounted INSIDE the Eval Settings panel, which is an
  // `<aside aria-hidden width:0>` when collapsed (`eval-settings-panel.tsx`) — so opening the
  // dialog from a date cell while the panel is closed would render the modal under an
  // aria-hidden, clipped ancestor. Open the panel alongside the dialog so it renders in a
  // visible, reachable region (INS-12497).
  const openEvalSettingsPanel = useSetAtom(evalSettingsPanelOpenAtom);
  // Pre-filters the schedule to the clicked property on the next open (INS-12497): the pencil
  // sets this to the row's slot id, the dialog seeds its search from it and clears it.
  const setScheduleFocus = useSetAtom(scheduleFocusSearchAtom);
  // Opens the schedule dialog focused on one property: open the (mounting) panel, seed the
  // "focus this well" filter with the row's slot id, then open the dialog. A slotId-less row
  // focuses nothing (opens the full schedule).
  const openScheduleForSlot = useCallback(
    (slotId: string | undefined) => {
      openEvalSettingsPanel(true);
      setScheduleFocus(slotId ?? null);
      openScheduleDialog(true);
    },
    [openEvalSettingsPanel, setScheduleFocus, openScheduleDialog]
  );
  // INS-12871 — Properties opens with the Valued = Yes filter pre-applied, but ONLY on a run
  // evaluation (calc mode). `valued` is eval-scoped: filtering it with no calculationId
  // (package / draft mode) 422s, so there the default is omitted and the grid opens unfiltered.
  const valuedDefaultApplies = calculationId !== null;
  const {
    rows: baseRows,
    totalCount,
    isFetching,
    error,
    pagination,
    onPaginationChange,
    onSortChange,
    onGlobalSearchChange,
    onColumnFilterChange,
    queryKey,
    filterScope,
  } = useMasterDataView({
    view: 'slots',
    packageId,
    calculationId,
    slotOverrides,
    readSlotOverrides,
    initialTableFilters: valuedDefaultApplies
      ? DEFAULT_VALUED_TABLE_FILTERS
      : undefined,
    // INS-12871 — the Valued filter is eval-scoped, so re-seed the wire filter (and re-key the
    // DataTable below) when the eval changes in place, clearing a stale `valued` that would 422.
    resetTableFiltersOnCalcChange: true,
  });

  const { appendSlotOverride, setScheduleSlotOverrides } = workingConfig;
  const queryClient = useQueryClient();

  // Per-cell rejections from the last Calculate (INS-12497): a locked/out-of-order date
  // override the run refused. Read to mark the exact cell rejected instead of leaving its
  // optimistic ✓, and cleared per-cell when the user re-edits it (below).
  //
  // KNOWN GAP (INS-12392): a rejection is keyed by the id the dialog submitted — the DPS
  // `unique_slot_id` (`well_<uwi>` for a well) — while a grid row's `slotId` is the bare UWI.
  // So for a WELL row (including the DUC in the ticket) this lookup misses and the per-cell
  // marker doesn't paint; the aggregate rejection toast still fires. Matching the two id spaces
  // needs the slots view to project `unique_slot_id` onto the row, tracked under INS-12392.
  const scheduleRejections = useAtomValue(scheduleOverrideRejectionsAtom);
  const setScheduleRejections = useSetAtom(scheduleOverrideRejectionsAtom);
  const rejectedCells = useMemo(() => {
    const byCell = new Map<string, SlotOverrideRejection['code']>();
    for (const rejection of scheduleRejections) {
      byCell.set(`${rejection.slotId}:${rejection.field}`, rejection.code);
    }
    return byCell;
  }, [scheduleRejections]);

  const [edit, setEdit] = useState<SlotEdit | null>(null);

  // Bulk edit: which slots are selected (snapshotted by id so the selection
  // survives server-pagination) and whether the bulk panel is open.
  const [selectedById, setSelectedById] = useState<Record<string, SlotRow>>({});
  const [bulkOpen, setBulkOpen] = useState(false);
  // The table's raw select-all flag: `false` = enumerated (individual checkboxes,
  // `selectedById`); `true` = header select-all, where `excludedIds` are the deselected
  // slots. Drives the panel scope + the global-vs-per-slot save routing. Only meaningful
  // because the table is server-driven (see the DataTable `scroll` prop).
  const [selectAll, setSelectAll] = useState(false);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  // Select-all AFE picker (INS-10777 P3): the chosen catalog AFE + the debounced search
  // text driving `useAfeCatalogSearch`. Owned here (not the panel form) since the global
  // AFE isn't a per-cell form value.
  const [selectedAfe, setSelectedAfe] = useState<AvailableAfe | null>(null);
  const [afeSearch, setAfeSearch] = useState('');

  // The BE resolves `slotOverrides` server-side; the schedule dialog's date edits
  // (`scheduleSlotOverrides`) are overlaid client-side here so a dialog edit reflects LIVE
  // in the grid — the mirror of view-property-schedule's `deriveSlotDateOverrides`.
  const rows = useMemo(
    () =>
      overlayScheduleDateOverrides(
        baseRows as SlotRow[],
        scheduleSlotOverrides ?? []
      ),
    [baseRows, scheduleSlotOverrides]
  );

  // The slot whose valuation tearsheet is open (INS-11969), or null. Local to this
  // view — the tearsheet is a slot-scoped detail of the Properties table.
  const [openSlotId, setOpenSlotId] = useState<string | null>(null);

  const handleOpenTearsheet = useCallback((row: SlotRow) => {
    if (row.slotId) {
      setOpenSlotId(row.slotId);
    }
  }, []);

  const closeTearsheet = useCallback(() => setOpenSlotId(null), []);
  // ── THE ROUTE OUT OF A PENDING OVERRIDE ────────────────────────────────────────────────
  //
  // The tearsheet tells a user their override "applies when this evaluation is next
  // calculated" and, until now, left them to work out where that happens. This takes them
  // there: close the modal and make sure the Eval Settings panel — which holds the Calculate
  // button — is open behind it. The panel defaults to open, so on the common path this is
  // just the close; setting it covers the user who had collapsed it.
  //
  // IT DOES NOT CALCULATE. Firing the run from here would mean choosing, on the user's
  // behalf, between updating this evaluation in place and forking a new one — and the
  // working config's default is the in-place branch, which nulls `calculation_id` through
  // `update_columns` and orphans the previous run's blobs. That choice already
  // has a home, a checkbox and copy of its own in the settings panel; this routes the user
  // to it rather than pre-empting it.
  const setEvalSettingsPanelOpen = useSetAtom(evalSettingsPanelOpenAtom);
  const routeToCalculate = useCallback(() => {
    setOpenSlotId(null);
    setEvalSettingsPanelOpen(true);
  }, [setEvalSettingsPanelOpen]);

  // The page's slot ids, in table order — drives the tearsheet's prev/next slot
  // navigation.
  const slotIds = useMemo(
    () =>
      rows.map((row) => row.slotId).filter((id): id is string => Boolean(id)),
    [rows]
  );

  // Current-page overlaid rows keyed by slotId — the lookup that lets us capture
  // a real row object the moment it's ticked.
  const rowsById = useMemo(() => {
    const map = new Map<string, SlotRow>();
    rows.forEach((row) => {
      if (row.slotId) {
        map.set(String(row.slotId), row);
      }
    });
    return map;
  }, [rows]);

  const selectedIds = useMemo(() => Object.keys(selectedById), [selectedById]);

  // Reconcile the snapshot map against the table's selection on every change:
  // capture newly-ticked rows from the current page, drop deselected ones.
  const onSelectionChange = useCallback(
    (state: TableState) => {
      const { selectAll: nextSelectAll, toggledNodes } = state.selection;
      setSelectAll(nextSelectAll);
      if (nextSelectAll) {
        // Select-all mode: toggledNodes are the EXCLUDED slots. Drop any enumerated
        // snapshot so a prior tick-selection can't linger behind the select-all scope.
        setExcludedIds(toggledNodes);
        setSelectedById({});
      } else {
        // Enumerated mode: toggledNodes are the picked slots — snapshot them so the
        // selection survives server-pagination. Leaving select-all drops any global AFE
        // pick so it can't leak into the enumerated flow.
        setExcludedIds([]);
        setSelectedAfe(null);
        setSelectedById((prev) =>
          reconcileSelectedRows(prev, toggledNodes, rowsById)
        );
      }
    },
    [rowsById]
  );

  // Real rows for every selected slot: the live overlaid row when it's on the
  // current page, else the captured snapshot. Gives the panel a complete
  // apply-list AND real review originals for on- and off-page slots alike.
  const selectedRows = useMemo<SlotRow[]>(
    () =>
      selectedIds
        .map((id) => rowsById.get(id) ?? selectedById[id])
        .filter((row): row is SlotRow => Boolean(row)),
    [selectedIds, rowsById, selectedById]
  );

  // INS-11129 — the volume-source picker's narrowing state (dropdowns 1 and 2) and the
  // forecast list it keys. Owned here, not by the panel, so the panel stays presentational.
  // Narrowing only shortens that list — the forecast the user lands on applies to the WHOLE
  // selection, exactly like the AFE override, so none of this scopes which slots change.
  const { picker: bulkVolumeSource, reset: resetBulkVolumeSource } =
    useVolumeSourcePickerData({ enabled: bulkOpen });

  // AFE candidates valid for ALL selected slots; fetched only while the panel is
  // open (frugal — selecting rows to eyeball costs nothing).
  const bulkAfeOptions = useBulkAvailableAfes(selectedIds, {
    // Enumerated mode only — the AFE intersection can't run over "all" slots; the
    // select-all path uses the catalog search below instead.
    enabled: bulkOpen && !selectAll,
  });

  // Select-all AFE candidates from the catalog (server-searched, infinite scroll). Mutually
  // exclusive with `bulkAfeOptions` via `enabled`.
  const afeCatalog = useAfeCatalogSearch(afeSearch, {
    enabled: bulkOpen && selectAll,
  });
  // Debounce the search text out of the picker's input handler (not a useEffect).
  const debouncedSetAfeSearch = useMemo(() => debounce(setAfeSearch, 400), []);
  // Cancel a pending debounced search on unmount so it can't fire setState after teardown.
  useEffect(
    () => () => debouncedSetAfeSearch.cancel(),
    [debouncedSetAfeSearch]
  );

  // Closing the panel (Cancel / X / ESC, without saving) abandons the bulk
  // action entirely: clear the selection. Combined with rendering the panel only
  // while open, the in-progress draft is discarded too, so a reopen always starts
  // clean. Selection still persists across search/sort/pagination during the
  // *selection* phase (before the panel is opened).
  const closeBulk = useCallback(() => {
    setBulkOpen(false);
    setSelectedById({});
    setSelectAll(false);
    setExcludedIds([]);
    setSelectedAfe(null);
    setAfeSearch('');
    resetBulkVolumeSource();
    // Drop a pending debounced search so it cannot re-set stale text after the reset (the
    // volume picker's reset does the same for its own).
    debouncedSetAfeSearch.cancel();
  }, [debouncedSetAfeSearch, resetBulkVolumeSource]);

  // INS-10777 — commit one override operation: optimistically paint the current page (no
  // fetch), mark the other slots-view pages stale (they reconcile on the next navigation),
  // then append the operation to the working config (drives Calculate + serialization).
  const commitSlotOverride = useCallback(
    (operation: SlotOverrideOperation) => {
      const prev =
        queryClient.getQueryData<MasterDataResponse<SlotRow>>(queryKey);
      if (prev) {
        queryClient.setQueryData<MasterDataResponse<SlotRow>>(queryKey, {
          ...prev,
          rows: paintNewestOperation(prev.rows, operation),
        });
      }
      // Append, prune the schedule-dialog channel, and invalidate: the same steps the agent
      // path takes, through the one helper both call. A composite wellbore's leg is painted
      // but its row's recipe is the server's to fold, so that operation refetches the grid.
      commitOverrideToWorkingConfig(queryClient, packageId, operation, {
        appendSlotOverride,
        readScheduleSlotOverrides: () => scheduleSlotOverrides ?? [],
        setScheduleSlotOverrides,
        targetsCompositeLeg: targetsCompositeLeg(prev?.rows ?? [], operation),
      });
      // Re-editing a cell supersedes any prior Calculate rejection on it, so drop the stale
      // "rejected" mark now rather than leave it until the next run.
      setScheduleRejections((prevRejections) =>
        clearRejectionsForOperation(prevRejections, operation)
      );
    },
    [
      queryClient,
      queryKey,
      packageId,
      appendSlotOverride,
      scheduleSlotOverrides,
      setScheduleSlotOverrides,
      setScheduleRejections,
    ]
  );

  // KNOWN GAP (INS-12392): Bulk Edit still offers Spud / Permit / First Production, which commit
  // as `bulk` / `filter` / `ids` ops. `OverrideWriteValidator` only checks `individual` entries,
  // so a bulk milestone write on a DUC gets no 422 and `paintNewestOperation` still paints the ✓
  // — the ticket's symptom via a second entry point. The consistent fix (drop the three
  // milestones from the shared `bulk-edit-slots` component, leaving the schedule dialog as the
  // one place milestones are edited) touches a component shared beyond eval3, so it is tracked
  // under INS-12392 rather than done here. AC1's read-only cells cover the inline path only.
  const handleBulkEditSave = useCallback(
    (payload: BulkEditScopedSavePayload) => {
      if (payload.scope === 'all') {
        // Select-all → a filter operation scoped to the active filter (snapshotted at Save)
        // minus the manual exclusions. The AFE comes from THIS component's `selectedAfe`.
        commitSlotOverride({
          kind: 'filter',
          scope: filterScope,
          excludedIds,
          overrides: {
            ...payload.patch,
            ...(selectedAfe ? { afeSourceId: String(selectedAfe.id) } : {}),
          },
          // Paint the chosen AFE's well name + catalog href so the AFE Well Name column
          // and AFE Source link stay aligned with the new AFE (display-only; not serialized).
          ...(selectedAfe
            ? {
                displayPatch: {
                  afeWellName: selectedAfe.well?.name,
                  afeSourceHref: selectedAfe.well?.href,
                },
              }
            : {}),
        });
      } else {
        // Checkbox selection → an id-list operation over exactly the selected slots.
        commitSlotOverride({
          kind: 'ids',
          slotIds: selectedIds,
          overrides: { ...payload.patch },
        });
      }
      // Same teardown as a cancel — clear the selection + close (fresh start).
      closeBulk();
    },
    [
      commitSlotOverride,
      filterScope,
      excludedIds,
      selectedAfe,
      selectedIds,
      closeBulk,
    ]
  );

  const openVolumeEdit = useCallback(
    (row: SlotTableRow, options: { leg?: boolean } = {}) => {
      if (!row.slotId) {
        return;
      }
      const overridden = Boolean(
        row.overriddenFields?.includes('volumeSourceId')
      );
      // `currentId` is the OVERRIDE only — the BE-resolved row carries the override iff
      // `volumeSourceId` is flagged. The picker modal pre-selects it, else the row's resolved
      // source from `pair` (INS-13809 AC 2).
      setEdit({
        kind: 'volume',
        slotId: row.slotId,
        wellName: row.wellName ?? undefined,
        isWellSlot: row.isWellSlot === true,
        currentId: overridden ? (row.volumeSourceId ?? undefined) : undefined,
        // The type comes from the row, else from an override in the working config that set
        // this source — read at click time, the log as it is now (see readSlotOverrides).
        currentSource: currentVolumeSourceForRow(
          row,
          form.state.values.slotOverrides,
          form.state.values.committedSlotOverrides
        ),
        // Via `pairFromRow`: `SlotTableRow`'s index signature would let a misspelled field compile.
        pair: pairFromRow(row),
        // A leg's override is dropped from the same drawer that set it (`legEditRow` flags the
        // leg's own override on the row it hands over).
        resettable: Boolean(options.leg) && overridden,
      });
    },
    [form]
  );

  const openAfeEdit = useCallback((row: SlotTableRow) => {
    if (!row.slotId) {
      return;
    }
    // AFE seeds with the resolved row value (override-or-base — the BE resolves it).
    setEdit({
      kind: 'afe',
      slotId: row.slotId,
      wellName: row.wellName ?? undefined,
      currentId: row.afeSourceId ?? undefined,
    });
  }, []);

  const columns = useMemo<DataTableColumnDef<SlotTableRow>[]>(() => {
    /**
     * Cost/slot date column (override-aware). Two shapes, chosen by `affordance`:
     *
     *   'edit' — the PROD inline-editable cell: click the pencil to edit the date in place,
     *     committing a per-slot override; a green checkmark marks an overridden value. Used by
     *     the cost dates and any other non-milestone editable date.
     *   'schedule' — a READ-ONLY cell whose trailing pencil opens the "View property schedule"
     *     Gantt dialog, for the four lifecycle milestones (permit, spud, completion, first
     *     production) — INS-12497. It keeps the rejection/override marker a refused Calculate
     *     leaves.
     *
     * `affordance` is REQUIRED (no default): a milestone must not silently fall back to the
     * inline-editable shape AC1 rules out — a new milestone column is a compile error until it
     * declares `'schedule'` explicitly (INS-12497 review).
     *
     * @param {string} accessorKey - The date field.
     * @param {string} header - The column header.
     * @param {number} size - The column width in px.
     * @param {'edit' | 'schedule'} affordance - Which cell shape to render (see above).
     * @return {object} The DataTable column definition.
     */
    const dateColumn = (
      accessorKey: CostDateField | SlotDateField,
      header: string,
      size: number,
      affordance: 'edit' | 'schedule'
    ): DataTableColumnDef<SlotTableRow> => ({
      accessorKey,
      header,
      size,
      // The header still gets the Start/End range filter: `type: 'date'` selects the date
      // filter variant, and `dateFilterLocalTime` the local-time single-range picker — same as
      // the display-only date columns. The custom `cell` below owns rendering.
      meta: { type: 'date', dateFilterLocalTime: true },
      cell: (info) => {
        const row = info.row.original;
        const slotId = row.slotId;
        if (affordance === 'schedule') {
          // Milestone cell: read-only date + rejection/override marker + schedule pencil. A
          // Calculate rejection wins over the optimistic ✓: a write the run refused must read
          // as rejected, never as an applied override (INS-12497).
          const cellState = slotDateCellState({
            slotId,
            accessorKey,
            overriddenFields: row.overriddenFields,
            rejectedCells,
          });
          return (
            <div className={styles.dateCell}>
              <EditableDateCell
                value={info.getValue<string | undefined>()}
                canEdit={false}
                adornment={
                  cellState.kind === 'rejected' ? (
                    <span
                      className={styles.rejectedIconWrap}
                      title={REJECTION_REASON[cellState.code]}
                    >
                      <WarningAltFilled
                        className={styles.rejectedIcon}
                        aria-label={`${header} edit rejected: ${
                          REJECTION_REASON[cellState.code]
                        }`}
                      />
                    </span>
                  ) : cellState.kind === 'overridden' ? (
                    <Checkmark
                      className={styles.overrideIcon}
                      aria-label={`${header} overridden`}
                    />
                  ) : null
                }
                // Inert: never called while `canEdit` is false (the cell is display-only).
                onCommit={() => undefined}
              />
              {/* Opens the "View property schedule" Gantt, focused on this property (INS-12497)
                  — disabled until an evaluation case is selected (it can't render without one).
                  No inline date pencil here. */}
              <DateCellScheduleButton
                header={header}
                disabled={!caseSelected}
                onOpen={() => openScheduleForSlot(slotId)}
              />
            </div>
          );
        }
        // Default (prod): inline-editable date cell (click-to-edit, override-aware).
        const isOverridden = Boolean(
          slotId && row.overriddenFields?.includes(accessorKey)
        );
        return (
          <EditableDateCell
            value={info.getValue<string | undefined>()}
            canEdit={Boolean(slotId)}
            // The "overridden" decoration is a Slot-view concern: a green checkmark on the cell.
            adornment={
              isOverridden ? (
                <Checkmark
                  className={styles.overrideIcon}
                  aria-label={`${header} overridden`}
                />
              ) : null
            }
            onCommit={(value) => {
              if (!slotId) {
                return;
              }
              commitSlotOverride({
                kind: 'slot',
                slotId,
                overrides: { [accessorKey]: value },
              });
            }}
          />
        );
      },
    });

    const base: DataTableColumnDef<SlotTableRow>[] = [
      {
        accessorKey: 'slotId',
        header: 'Slot ID',
        size: 176,
        // Two controls: the display value (the well's UWI when present) links to the WELL
        // PAGE, and the trailing icon opens the valuation tearsheet. The column stays keyed
        // on `slotId` (the row identity) for sort/selection. Well Name links to the well
        // too, deliberately — see SlotIdCell.
        cell: (info) => (
          <SlotIdCell
            row={info.row.original}
            onOpen={handleOpenTearsheet}
            disabled={calculationId === null}
          />
        ),
      },
      {
        accessorKey: 'assetType',
        header: 'Asset Type',
        size: 120,
        // Sortable, but a server no-op until the BE projects `assetType` (the
        // cell falls back to "Tract" today).
        cell: (info) => formatAssetType(info.getValue<string>()),
      },
      {
        accessorKey: 'volumeSourceId',
        header: 'Volume Source',
        size: 200,
        // A composite wellbore's legs each take a line; the table measures a wrapping row.
        meta: { wrap: true },
        cell: (info) => {
          const row = info.row.original;
          const slotId = row.slotId;
          const isOverridden = rowVolumeSourceOverridden(row);
          // A composite wellbore lists each leg's curve on its own line, with its own pencil:
          // the override is per leg, keyed on the leg's own slot id (legEditRow), and the ✓
          // marks the overridden leg. A whole-row override (a bulk edit that selected the
          // composite) prices the well on one curve, so the row then reads like any other.
          if (row.compositeWellboreLegs && !isOverridden) {
            return (
              <CompositeLegLines
                legs={row.compositeWellboreLegs}
                render={(leg) => (
                  <EditableSourceCell
                    isOverridden={leg.overridden}
                    overriddenLabel={`Volume source overridden for ${leg.slotId}`}
                    displayValue={leg.volumeSourceId ?? undefined}
                    href={leg.volumeSourceHref ?? undefined}
                    editLabel={`Edit Volume Source for ${leg.slotId}`}
                    canEdit={Boolean(slotId)}
                    onEdit={() =>
                      openVolumeEdit(legEditRow(row, leg), { leg: true })
                    }
                    wrapText
                  />
                )}
              />
            );
          }
          // The server names every row, including overridden ones; a DCA forecast shows its
          // business id.
          const displayValue =
            row.volumeSourceName ?? row.volumeSourceId ?? undefined;
          const isTypeCurve = row.volumeSourceType === 'type_curve';
          return (
            <EditableSourceCell
              isOverridden={isOverridden}
              overriddenLabel="Volume source overridden"
              displayValue={displayValue}
              // Link only for type-curve sources that carry an href.
              href={isTypeCurve ? row.volumeSourceHref : undefined}
              leading={
                isTypeCurve ? (
                  // Type-curve verification status (renders nothing unless decided).
                  <TypeCurveVerificationStatusIndicator
                    status={row.typeCurveVerificationStatus}
                    showLabel={false}
                    hoverVerifiedBy
                    lastVerifiedBy={row.typeCurveLastVerifiedBy}
                    className={styles.volumeSourceVerificationIcon}
                    iconClasses={{
                      correct: styles.verificationStatusCorrect,
                      incorrect: styles.verificationStatusIncorrect,
                    }}
                  />
                ) : undefined
              }
              editLabel="Edit Volume Source"
              canEdit={Boolean(slotId)}
              onEdit={() => openVolumeEdit(row)}
            />
          );
        },
      },
      {
        ...column('forecastStatus', 'Forecast Status', 150, 'text'),
        // A composite wellbore's legs each take a line, one per curve the Volume Source cell
        // lists. Under a whole-row override that cell names one source, so this one shows the
        // row's own status beside it rather than a line per leg.
        cell: (info) => {
          const row = info.row.original;
          return row.compositeWellboreLegs &&
            !rowVolumeSourceOverridden(row) ? (
            <CompositeLegLines
              legs={row.compositeWellboreLegs}
              render={(leg) =>
                formatCellValue(leg.forecastStatus, { type: 'text' })
              }
            />
          ) : (
            formatCellValue(info.getValue(), { type: 'text' })
          );
        },
      },
      column('forecastDate', 'Forecast Date', DATE_COLUMN_SIZE, 'date'),
      column('dcaScenario', 'DCA Scenario', 150, 'text'),
      areaColumn('expenseArea', 'expenseAreaHref', 'Expense Area', 150),
      column('slotLength', 'Slot Length', 130, 'integer'),
      {
        ...column('typeCurveAreaName', 'Type Curve Area', 160, 'text'),
        // The AREA has no page (/type_curve_areas/:id is JSON-only), so the cell
        // links to the type CURVE that priced the slot. See typeCurveAreaLink.
        cell: (info) => (
          <LinkCell
            value={info.getValue<string>()}
            href={typeCurveAreaLink(info.row.original)}
          />
        ),
      },
      // Scenario-area display names (parity with the legacy slot-allocations table);
      // populated in calc mode, blank in package/live mode (area assignment is
      // calc-scoped).
      {
        ...areaColumn('dpsArea', 'dpsAreaHref', 'DPS Area', 160),
        cell: (info) => <DpsAreaCell row={info.row.original} />,
      },
      areaColumn('taxArea', 'taxAreaHref', 'Tax Area', 160),
      areaColumn(
        'differentialArea',
        'differentialAreaHref',
        'Differential Area',
        170
      ),
      areaColumn(
        'discountRateArea',
        'discountRateAreaHref',
        'Discount Rate Area',
        180
      ),
      column('typeCurveLateralLength', 'TC Lateral Length', 150, 'integer'),
      {
        accessorKey: 'afeSourceId',
        header: 'AFE Source',
        size: 180,
        cell: (info) => {
          const row = info.row.original;
          const slotId = row.slotId;
          return (
            <EditableSourceCell
              isOverridden={Boolean(
                slotId && row.overriddenFields?.includes('afeSourceId')
              )}
              overriddenLabel="AFE source overridden"
              displayValue={row.afeSourceId ?? undefined}
              href={row.afeSourceHref}
              editLabel="Edit AFE Source"
              canEdit={Boolean(slotId)}
              onEdit={() => openAfeEdit(row)}
            />
          );
        },
      },
      column('afeWellName', 'AFE Well Name', 180, 'text'),
      column(
        'slotContingenciesCosts',
        'Slot Contingencies Costs',
        200,
        'currency'
      ),
      dateColumn(
        'contingenciesCostsDate',
        FIELD_LABELS.contingenciesCostsDate,
        DATE_COLUMN_SIZE,
        'edit'
      ),
      column('slotFacilitiesCosts', 'Slot Facilities Costs', 180, 'currency'),
      dateColumn(
        'facilitiesCostsDate',
        FIELD_LABELS.facilitiesCostsDate,
        DATE_COLUMN_SIZE,
        'edit'
      ),
      column('slotDrillingCosts', 'Slot Drilling Costs', 170, 'currency'),
      dateColumn(
        'drillingCostsDate',
        FIELD_LABELS.drillingCostsDate,
        DATE_COLUMN_SIZE,
        'edit'
      ),
      column('slotCompletionCosts', 'Slot Completion Costs', 180, 'currency'),
      dateColumn(
        'completionCostsDate',
        FIELD_LABELS.completionCostsDate,
        DATE_COLUMN_SIZE,
        'edit'
      ),
      column('slotOtherCosts', 'Slot Other Costs', 150, 'currency'),
      dateColumn(
        'otherCostsDate',
        FIELD_LABELS.otherCostsDate,
        DATE_COLUMN_SIZE,
        'edit'
      ),
      ...unitAndInterestColumns<SlotTableRow>(),
      column('interestType', 'Interest Type', 140, 'text'),
      column('unitSource', 'Unit Source', 140, 'text'),
      column('unitDevelopmentType', 'Unit Development Type', 170, 'text'),
      {
        accessorKey: 'wellName',
        header: 'Well Name',
        size: 180,
        cell: (info) =>
          linkCellRenderer(
            info.getValue<string>(),
            slotIdLink(info.row.original)
          ),
      },
      wellStatusColumn<SlotTableRow>(),
      column('typeCurveStatus', 'Type Curve Status', 150, 'text'),
      column('operator', 'Operator', 180, 'text'),
      column('holeDirection', 'Hole Direction', 140, 'text'),
      {
        ...column('formation', 'Formation', 160, 'text'),
        // A composite wellbore's legs each take a line, one per curve the Volume Source cell
        // lists (master_data sends legs for no other row); under a whole-row override that cell
        // names one source, so this one shows the landing formation. Every other row renders as
        // the plain text column does.
        cell: (info) => {
          const row = info.row.original;
          return row.compositeWellboreLegs &&
            !rowVolumeSourceOverridden(row) ? (
            <CompositeLegLines
              legs={row.compositeWellboreLegs}
              render={(leg) => formatCellValue(leg.formation, { type: 'text' })}
            />
          ) : (
            formatCellValue(info.getValue(), { type: 'text' })
          );
        },
      },
      column('stateAbbreviation', 'State', 90, 'text'),
      column('basin', 'Basin', 130, 'text'),
      column('tier', 'Tier', 100, 'text'),
      column('trueVerticalDepthFeet', 'TVD (ft)', 120, 'integer'),
      // Slot number is an identifier, not a quantity — render as text (no commas).
      column('slotNumber', 'Slot #', 100, 'text'),
      // Date cluster order matches ENHANCED_OVERVIEW_SLOT_COLUMNS:
      // permit, spud, rigRelease, completion, firstProduction, shutIn. The four lifecycle
      // milestones (permit, spud, completion, first production) carry the schedule pencil
      // (INS-12497); rig-release and shut-in stay plain read-only dates.
      dateColumn('permitDate', 'Permit Date', DATE_COLUMN_SIZE, 'schedule'),
      dateColumn('spudDate', 'Spud Date', DATE_COLUMN_SIZE, 'schedule'),
      column('rigReleaseDate', 'Rig Release Date', DATE_COLUMN_SIZE, 'date'),
      {
        // Read-only reported completion (the modeled/override completion lives in the schedule
        // dialog, not here), so it shows NO override ✓ — there is no grid completion override
        // source (GRID_SYNCED_MILESTONES excludes it). It DOES surface a Calculate rejection,
        // though: a refused completion date-override (422 out_of_order / locked_reported, wire
        // field `completion_date`) must read as rejected on this exact cell, not vanish into the
        // aggregate toast (INS-12497 review). `slotDateCellState`/`wireFieldFor` don't map the
        // grid's `completionDate` accessor, so look the rejection up by its wire field directly.
        // Joins the milestone cluster with the schedule pencil; keeps the inline range filter.
        accessorKey: 'completionDate',
        header: 'Completion Date',
        size: DATE_COLUMN_SIZE,
        meta: { type: 'date', dateFilterLocalTime: true },
        cell: (info) => {
          const slotId = info.row.original.slotId;
          const rejectionCode = slotId
            ? rejectedCells.get(`${slotId}:completion_date`)
            : undefined;
          return (
            <div className={styles.dateCell}>
              <EditableDateCell
                value={info.getValue<string | undefined>()}
                canEdit={false}
                adornment={
                  rejectionCode ? (
                    <span
                      className={styles.rejectedIconWrap}
                      title={REJECTION_REASON[rejectionCode]}
                    >
                      <WarningAltFilled
                        className={styles.rejectedIcon}
                        aria-label={`Completion Date edit rejected: ${REJECTION_REASON[rejectionCode]}`}
                      />
                    </span>
                  ) : null
                }
                onCommit={() => undefined}
              />
              <DateCellScheduleButton
                header="Completion Date"
                disabled={!caseSelected}
                onOpen={() => openScheduleForSlot(slotId)}
              />
            </div>
          );
        },
      },
      dateColumn(
        'firstProductionDate',
        'First Production Date',
        DATE_COLUMN_SIZE,
        'schedule'
      ),
      column('shutInDate', 'Shut In Date', DATE_COLUMN_SIZE, 'date'),
      {
        accessorKey: 'valued',
        header: 'Valued',
        size: 100,
        // INS-12871 — shared Yes/No filter meta + cell (VALUED_FILTER_META / valuedCell in
        // valued-column-utils, also used by Property Allocations). The filter is eval-scoped, so offer it
        // ONLY in calc mode: a `valued` filter with no calculationId 422s (requires_eval_calc),
        // the same reason the default seed is gated — so one-click Yes/No can't hit that error.
        enableColumnFilter: valuedDefaultApplies,
        meta: VALUED_FILTER_META,
        cell: valuedCell,
      },
      unvaluedReasonColumn<SlotTableRow>(packageId),
    ];

    // Every column wraps long content; preserve each column's existing `meta`.
    // Every data column is sortable. Note: the override-overlaid columns (volume /
    // AFE cascades, cost amounts, cost + slot dates) sort on the server's BASE
    // value, so their order can diverge from the displayed `override ?? base`
    // after an in-cell edit. The pin cell reads/writes `locatedRowAtom` itself,
    // so its column is arg-free.
    return [
      locateColumn<SlotTableRow>(),
      ...base.map((col) => ({
        ...col,
        meta: { ...col.meta, wrap: true, disableFilterAutocomplete: true },
      })),
    ];
  }, [
    openVolumeEdit,
    openAfeEdit,
    commitSlotOverride,
    handleOpenTearsheet,
    calculationId,
    valuedDefaultApplies,
    rejectedCells,
    caseSelected,
    openScheduleForSlot,
    packageId,
  ]);

  const closeEdit = useCallback(() => setEdit(null), []);

  // Publish the columns this table shows, in their on-screen order, to the shared atom so the
  // content export's "Current View" preset exports the layout on screen.
  const handleVisibleColumnsChange = useMasterDataVisibleColumns(
    'slots',
    columns
  );

  // Inline column filters (INS-11272): translate the DataTable's ColumnFiltersState
  // to the `tableFilters` wire shape and hand it up with the snapshot's reset page.
  const onFilterChange = useCallback(
    (state: TableState) =>
      onColumnFilterChange(
        toTableFilters(state.columnFilters, columns, 'slots'),
        state.page
      ),
    [onColumnFilterChange, columns]
  );

  return (
    <div className={styles.slotTableWrapper}>
      <DataTable<SlotTableRow>
        // INS-12871 — re-key on the calc identity so the table re-seeds its inline column
        // filters whenever `useMasterDataView` resets the wire half on a calc change; the two
        // halves then can't disagree (a stale eval-scoped `valued` would otherwise ride the next
        // filter edit and 422). keepPreviousData + the controlled pagination/selection survive a
        // table remount, and the persisted column layout re-hydrates from `persistenceKey`.
        key={calculationId ?? 'draft'}
        data={rows as SlotTableRow[]}
        columns={columns}
        // BE `id` === `slotId` for slots, so the slotId-keyed bulk-edit selection
        // (rowsById / reconcileSelectedRows / the controlled toggledNodes) stays
        // aligned with the DataTable row key.
        getRowId={(row) => String(row.id)}
        // `isFetching` so the refetch loading state shows under `keepPreviousData`.
        isLoading={isFetching}
        error={error ?? null}
        // Controlled pagination: useMasterDataView is the single page owner, so a
        // filter-driven reset to page 1 stays in sync with the pager display.
        // Controlled selection: slot-view owns the bulk-edit selection — in select-all
        // mode `toggledNodes` are the exclusions, else the picked slot ids — and a save
        // clears it by resetting that state. Merged with the controlled pagination; the
        // table reflects it and reports user clicks via `onSelectionChange`.
        controlledState={{
          pagination,
          selection: {
            selectAll,
            toggledNodes: selectAll ? excludedIds : selectedIds,
          },
        }}
        // INS-12871 — seed the Valued = Yes selection on the inline filter row so it matches
        // the server read (`initialTableFilters` above). The filter row itself is hidden until
        // the user opens it (`isColumnFilterActive` defaults to false), so the selection shows
        // once they do; it's mount-time only and user-removable, and empty off a run evaluation
        // (see `valuedDefaultApplies`).
        initialTableState={{
          columnFilters: valuedDefaultApplies
            ? DEFAULT_VALUED_COLUMN_FILTERS
            : undefined,
        }}
        persistenceKey="eval3-new-master-data-slot"
        config={{
          columnFilters: true,
          columnReordering: true,
          // Multi-select for bulk edit. The header select-all is ENABLED (default):
          // cross-page select-all uses `type: 'bulk'` entries in `slot_overrides`
          // with scope + excluded_slot_ids (no per-row materialization needed).
          // Load-bearing: the header only sets `selectAll: true` because the table is
          // server-driven (see `scroll` below).
          selectionType: 'multi',
        }}
        scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
        pageSizes={MASTER_DATA_PAGE_SIZES}
        onPaginationChange={onPaginationChange}
        onSortChange={onSortChange}
        onGlobalSearchChange={onGlobalSearchChange}
        onFilterChange={onFilterChange}
        onVisibleColumnsChange={handleVisibleColumnsChange}
        onSelectionChange={onSelectionChange}
        renderRowSubComponent={(row) => <SlotExpandedRow row={row.original} />}
        getRowCanExpand={(row) =>
          (row.original.tractAllocations?.length ?? 0) > 0
        }
        searchPlaceholder="Search properties..."
        leftToolbarActions={[
          <Fragment key="view-dropdown">{viewDropdown}</Fragment>,
        ]}
        rightToolbarActions={[
          <Button
            key="bulk-edit"
            kind="ghost"
            size="lg"
            hasIconOnly
            renderIcon={Edit}
            iconDescription="Bulk edit slots"
            tooltipPosition="bottom"
            disabled={
              selectAll
                ? excludedIds.length >= totalCount
                : selectedIds.length === 0
            }
            onClick={() => setBulkOpen(true)}
          />,
        ]}
      />

      <SlotTearsheet
        open={openSlotId !== null}
        onClose={closeTearsheet}
        packageId={packageId}
        calculationId={calculationId}
        slotId={openSlotId}
        slotIds={slotIds}
        onNavigate={setOpenSlotId}
        // The SAME handler the grid and both modals use. A tearsheet edit writes through this
        // and nothing else, so a tearsheet edit is indistinguishable downstream from a grid
        // edit — including the schedule-dialog channel prune it performs.
        onCommitOverride={commitSlotOverride}
        onRerun={routeToCalculate}
        // THE EDITS THE PANEL HAS TO BE ABLE TO SEE. Without this the tearsheet re-reads the
        // committed log only, so an override made from it never appears there — the "it does
        // not save" report. Same working-config log the grid sends with its own reads.
        slotOverrides={slotOverrides}
        // ⚠️ AND THE OTHER CHANNEL. The schedule dialog's date
        // edits live in a SECOND working-config list, which Calculate composes with the one
        // above and the tearsheet read did not. A date moved in the dialog left the panel's
        // picker clamping to a neighbour the run would not use, and Calculate then 422'd the
        // day the picker had just offered. The grid compensates client-side with
        // overlayScheduleDateOverrides; the tearsheet has no such overlay, so it needs the
        // channel itself.
        scheduleSlotOverrides={scheduleSlotOverrides}
      />

      {edit?.kind === 'volume' && (
        // Keyed on the slot: the modal seeds its narrowing and selection from the slot when it
        // mounts, so a different slot must mount a fresh one rather than inherit that state.
        <EditVolumeSourcePickerModal
          key={edit.slotId}
          slotId={edit.slotId}
          wellName={edit.wellName}
          isWellSlot={edit.isWellSlot}
          currentVolumeSourceId={edit.currentId}
          currentSource={edit.currentSource}
          pair={edit.pair}
          onClose={closeEdit}
          // A leg's revert: the volume family is cleared as one, as the tearsheet reverts a
          // row's (`VOLUME_OVERRIDE_FIELDS`), and the grid refetches as it does for any unset.
          onReset={
            edit.resettable
              ? () => {
                  commitSlotOverride({
                    kind: 'unset',
                    slotId: edit.slotId,
                    fields: [...VOLUME_OVERRIDE_FIELDS],
                  });
                  closeEdit();
                }
              : undefined
          }
          onSave={(selection) => {
            // The whole set: the routing fields eval_calculator picks the pathway from travel
            // with the id.
            commitSlotOverride({
              kind: 'slot',
              slotId: edit.slotId,
              overrides: volumeOverrides(selection),
            });
            closeEdit();
          }}
        />
      )}

      {edit?.kind === 'afe' && (
        <AfeEditConnector
          slotId={edit.slotId}
          wellName={edit.wellName}
          currentAfeSourceId={edit.currentId}
          onClose={closeEdit}
          onSave={(afeId, afeHref, _costs, afeWellName) => {
            commitSlotOverride({
              kind: 'slot',
              slotId: edit.slotId,
              overrides: { afeSourceId: afeId },
              // Paint the chosen AFE's well name + href so both columns stay aligned with
              // the new AFE (display-only; not serialized).
              displayPatch: { afeWellName, afeSourceHref: afeHref },
            });
            closeEdit();
          }}
        />
      )}

      {/* Rendered only while open so closing fully resets the panel (its draft
          unmounts) — closing is a clean abandon, paired with clearing the
          selection in `closeBulk`. SlotRow extends TractNRI, so the overlaid
          rows satisfy the panel's row contract. */}
      {bulkOpen && (
        <BulkEditSlotsPanel
          open
          onClose={closeBulk}
          onSave={handleBulkEditSave}
          volumeSource={bulkVolumeSource}
          scope={
            selectAll
              ? {
                  kind: 'all',
                  total: totalCount,
                  excludedCount: excludedIds.length,
                  afe: {
                    items: afeCatalog.items,
                    status: afeCatalog.status,
                    errorMessage: afeCatalog.errorMessage,
                    selectedAfe,
                    onSelect: setSelectedAfe,
                    onSearchChange: debouncedSetAfeSearch,
                    onLoadMore: afeCatalog.fetchNextPage,
                    hasNextPage: afeCatalog.hasNextPage,
                    isFetchingNextPage: afeCatalog.isFetchingNextPage,
                  },
                }
              : {
                  kind: 'enumerated',
                  selectedRows: selectedRows as TractNRI[],
                  allRows: rows as TractNRI[],
                  afeOptions: bulkAfeOptions,
                }
          }
        />
      )}
    </div>
  );
}

type AfeEditConnectorProps = Omit<
  EditAfeSourceModalViewProps,
  'data' | 'isLoading' | 'error' | 'isOpen'
>;

/**
 * Mounts the AFE Source modal with React-Query-fetched candidate AFEs. Rendered
 * only while the modal is open, so the fetch fires on open.
 *
 * @param {AfeEditConnectorProps} props - Slot context, close + save callbacks.
 * @return {JSX.Element} The connected AFE Source modal.
 */
function AfeEditConnector({
  slotId,
  wellName,
  currentAfeSourceId,
  onClose,
  onSave,
}: AfeEditConnectorProps) {
  const { data, isPending, error } = useAvailableAfes(slotId);
  return (
    <EditAfeSourceModalView
      isOpen
      onClose={onClose}
      onSave={onSave}
      slotId={slotId}
      wellName={wellName}
      currentAfeSourceId={currentAfeSourceId}
      data={data ?? null}
      isLoading={isPending}
      error={error?.message ?? null}
    />
  );
}
